'use client';

import React, { Fragment, useEffect, useState } from 'react';
import Slider from 'rc-slider';
import { Button, Card, Col, Collapse, Container, Form, Row } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { BiChevronDown, BiChevronUp, BiTrash } from 'react-icons/bi';
import { useTranslation } from '@/i18n/client';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { Locale } from '../../../../i18n-config';
import { useMediaQuery } from '@uidotdev/usehooks';
import Loading from '@/components/shared/Loading';
import ApiFailed from '@/components/shared/ApiFailed';
import Link from 'next/link';
import SearchTourButtons from './components/SearchTourButtons';
import { MdCompareArrows } from 'react-icons/md';

import 'rc-slider/assets/index.css';
import 'react-input-range/lib/css/index.css';
import Image from 'next/image';
import TourCard from '@/components/shared/TourCard';

const { createSliderWithTooltip } = Slider;
const Range = createSliderWithTooltip(Slider.Range);

const Checkbox = ({ type = 'checkbox', name, checked = false, onChange }) => {
  return <input type={type} name={name} checked={checked} onChange={onChange} />;
};

const Client = () => {
  const { lang } = useParams();
  const { t } = useTranslation(lang as Locale);
  const search = useSearchParams();
  const router = useRouter();

  const searchParams = new URLSearchParams(search);
  const travelStyleQuery = searchParams.get('style');

  const isMobile = useMediaQuery('only screen and (max-width : 768px)');
  const isTabletOrMobile = useMediaQuery('only screen and (min-width : 769px) and (max-width : 992px)');

  const checkboxes: Array<{ label: string; value: string }> = [
    {
      label: 'Group tours',
      value: 'Group_Tours',
    },
    {
      label: 'Family journey',
      value: 'FamilyJourneys',
    },
    {
      label: 'Seniors and Classic',
      value: 'Seniors-Classic',
    },
    {
      label: 'Nature adventure',
      value: 'NatureAdventure',
    },
    {
      label: 'Hiking and Trekking',
      value: 'Hiking&Trekking',
    },
    {
      label: 'Rail journey',
      value: 'RailJourneys',
    },
    {
      label: 'Nomads',
      value: 'Nomads',
    },
    {
      label: 'Ski ',
      value: 'ski',
    },
    {
      label: 'Photography',
      value: 'Photography',
    },
    {
      label: 'Health tourism',
      value: 'HealthTourism',
    },
    {
      label: 'Student tours',
      value: 'Student_tours_to_IRAN',
    },
    {
      label: 'Low budget offers',
      value: 'Low budget offers',
    },
  ];

  const dropDownLanguages = useQuery('drop-down-languages-2', () =>
    fetch('https://api.iranjourney.com/api/tour/language_in_tour').then((res) => res.json())
  );

  const { isLoading, isError, isSuccess, data, refetch } = useQuery(
    'tour_list_brief',
    () => fetch(`https://api.iranjourney.com/api/tour/tour_list_brief`).then((res) => res.json()),
    {
      onSuccess: (values) => {
        setAllFilterableTours(values);
      },
    }
  );

  const [open, setOpen] = useState<boolean>(false);
  const [compare_tours, setCompareTours] = useState<any[]>([]);
  const [isCompareSelected, setIsCompareSelected] = useState<any[]>([]);
  const [checkedItems, setCheckedItems] = useState<any[]>([]);
  const [map_view, setMapView] = useState<boolean>(false);
  const [show_advanced_search, setShowAdvancedSearch] = useState<boolean>(false);
  const [allFilterableTours, setAllFilterableTours] = useState<{
    group_tours: any[];
    custom_tours: any[];
    language: any[];
  }>({
    group_tours: [],
    custom_tours: [],
    language: [],
  });
  const [filterQueries, setFilterQueries] = useState<{
    language: string;
    duration: Array<number>;
    budget: Array<number>;
    travelStyles: any[];
  }>({
    language: '',
    duration: [1, 15],
    budget: [50, 3000],
    travelStyles: [],
  });

  const handleChange = (checkItem: any) => {
    if (checkedItems.includes(checkItem)) setCheckedItems([...checkedItems.filter((item) => item !== checkItem)]);
    else
      setCheckedItems((prev) => {
        return [...prev, checkItem];
      });
  };

  const shorten = (text, max) => {
    return text && text.length > max ? text.slice(0, max).split(' ').slice(0, -1).join(' ') : text;
  };

  const handleAddToCompare = (id, index) => {
    if (compare_tours.length < 5) {
      setCompareTours([...compare_tours, id]);

      const newClicks = [...isCompareSelected];
      newClicks[index] = !newClicks[index];

      setIsCompareSelected(newClicks);
    } else {
      alert('You have already selected 5 tours');
    }
  };

  const handleResetCompare = () => {
    setCompareTours([]);
    setIsCompareSelected([]);
    setOpen(false);
  };

  const handleRemoveFromCompare = (tourId) => {
    let clone = compare_tours.filter((item) => item !== tourId);

    if (clone.length > 0) setCompareTours([...compare_tours.filter((item) => item !== tourId)]);
    else handleResetCompare();
  };

  const handleUpdateFilters = (value, type) => {
    switch (type) {
      case 'language':
        setFilterQueries((prev) => {
          return { ...prev, language: value };
        });

        break;
      case 'duration':
        setFilterQueries((prev) => {
          return { ...prev, duration: [...value] };
        });

        break;
      case 'budget':
        setFilterQueries((prev) => {
          return { ...prev, budget: [...value] };
        });

        break;
      case 'travelStyles':
        setFilterQueries((prev) => {
          return { ...prev, travelStyles: [...value] };
        });

        break;
      default:
        break;
    }
  };

  const handleResetFilters = () => {
    var newURL = location.href.split('?')[0];
    router.push(newURL);

    searchParams.delete('style');

    setCheckedItems([]);

    setFilterQueries({
      language: '',
      duration: [1, 15],
      budget: [50, 3000],
      travelStyles: [],
    });
  };

  useEffect(() => {
    let filteredGroupTours: any[] = [];
    let filteredCustomTours: any[] = [];
    let filteredLanguages: any[] = [];

    if (isSuccess && data) {
      if (filterQueries.language === '') {
        filteredGroupTours = data.group_tours ? [...data.group_tours] : [];

        filteredCustomTours = data.custom_tours ? [...data.custom_tours] : [];
      }

      if (filterQueries.language !== '') {
        filteredLanguages = [
          ...allFilterableTours.language
            .filter((item: any) => item.language__code === filterQueries.language)
            .map((item: any) => item.id),
        ];

        filteredGroupTours = [...data.group_tours.filter((item) => filteredLanguages.includes(item.id))];

        filteredCustomTours = [...data.custom_tours.filter((item) => filteredLanguages.includes(item.id))];
      }

      if (filterQueries.duration.length > 0) {
        filteredGroupTours = [
          ...filteredGroupTours.filter(
            (item) => item.duration >= filterQueries.duration[0] && item.duration <= filterQueries.duration[1]
          ),
        ];

        filteredCustomTours = [
          ...filteredCustomTours.filter(
            (item) => item.duration >= filterQueries.duration[0] && item.duration <= filterQueries.duration[1]
          ),
        ];
      }

      if (filterQueries.budget.length > 0) {
        filteredGroupTours = [
          ...filteredGroupTours.filter(
            (item) => item.price >= filterQueries.budget[0] && item.price <= filterQueries.budget[1]
          ),
        ];

        filteredCustomTours = [
          ...filteredCustomTours.filter(
            (item) => item.price >= filterQueries.budget[0] && item.price <= filterQueries.budget[1]
          ),
        ];
      }

      if (filterQueries.travelStyles.length > 0) {
        let groupToursIdsWithTheseTags: any[] = [];
        let customToursIdsWithTheseTags: any[] = [];

        filteredGroupTours.forEach((element: any) => {
          element.tags.forEach((tagObj: any) => {
            if (filterQueries.travelStyles.includes(tagObj.name.toLowerCase())) {
              if (!groupToursIdsWithTheseTags.includes(element.id))
                groupToursIdsWithTheseTags = [...groupToursIdsWithTheseTags, element.id];
            }
          });
        });

        filteredCustomTours.forEach((element: any) => {
          element.tags.forEach((tagObj: any) => {
            if (filterQueries.travelStyles.includes(tagObj.name.toLowerCase())) {
              if (!customToursIdsWithTheseTags.includes(element.id))
                customToursIdsWithTheseTags = [...customToursIdsWithTheseTags, element.id];
            }
          });
        });

        filteredGroupTours = [...filteredGroupTours.filter((item) => groupToursIdsWithTheseTags.includes(item.id))];
        filteredCustomTours = [...filteredCustomTours.filter((item) => customToursIdsWithTheseTags.includes(item.id))];
      }

      setAllFilterableTours((prev) => {
        return {
          ...prev,
          group_tours: [...filteredGroupTours],
          custom_tours: [...filteredCustomTours],
        };
      });

      setShowAdvancedSearch(false);
    }
  }, [filterQueries, travelStyleQuery, isSuccess, data, allFilterableTours.language]);

  useEffect(() => {
    if (travelStyleQuery) {
      setCheckedItems([travelStyleQuery]);
      handleUpdateFilters([travelStyleQuery], 'travelStyles');
    } else setCheckedItems([]);
  }, [travelStyleQuery]);

  useEffect(() => {
    handleUpdateFilters(
      checkedItems.map((item: string) => item.toLowerCase()),
      'travelStyles'
    );
  }, [checkedItems]);

  let content;

  if (isLoading) content = <Loading />;

  if (isError) content = <ApiFailed refetch={refetch} />;

  if (isSuccess)
    content = (
      <Row className="g-3">
        <Fragment>
          <h1>Tours and expeditions</h1>

          {allFilterableTours.group_tours?.map((tour, index) => (
            <Col className="mb-3 tw-flex" xs={12} md={6} lg={4} key={tour.id}>
              <TourCard
                id={tour.id}
                image={`https://api.iranjourney.com/${map_view ? tour.map_thumbnail : tour.banner_thumbnail}`}
                title={tour.title}
                description={tour.overview}
                duration={tour.duration}
                price={tour.price}
                extraCation={
                  <span
                    className="tw-flex tw-items-center"
                    style={
                      isCompareSelected[index]
                        ? {
                            cursor: 'pointer',
                            color: '#521231',
                            fontWeight: 'bold',
                          }
                        : {
                            cursor: 'pointer',
                            color: 'black',
                          }
                    }
                    onClick={() => handleAddToCompare(tour.id, index)}
                  >
                    {' '}
                    <MdCompareArrows size={22} className="our_color pl-2 me-2" /> <span>{t('Compare')}</span>
                  </span>
                }
                badges={['group tour']}
                shouldImageBePriority={index === 0 ? true : false}
              />

              {/* <Card
                className="text-left tw-flex-1"
                style={
                  !isTabletOrMobile
                    ? {
                        display: 'inherit',

                        minHeight: '434px',
                        height: 'auto',
                        border: 'none',
                        boxShadow: '1px 2px #e3e1e1',
                      }
                    : {
                        display: 'inherit',
                        width: 'auto',
                        height: 'auto',
                        border: 'none',
                        boxShadow: '1px 2px #e3e1e1',
                      }
                }
              >
                <p
                  style={{
                    fontSize: '18px',
                    fontWeight: '550',
                  }}
                  className="montserrat mb-0 p-2"
                >
                  {' '}
                  {tour.title}
                </p>

                <div style={{ minHeight: '200px', height: '200px' }} className="tw-relative">
                  <Image
                    src={`https://api.iranjourney.com/${map_view ? tour.map_thumbnail : tour.banner_thumbnail}`}
                    alt={tour.title}
                    width={341}
                    height={183}
                    sizes="34vw"
                    className="tw-w-full"
                  />

                  <span
                    className="btn btn-sm pull-right mr-2 mt-2"
                    style={{
                      background: '#521231',
                      color: 'white',
                      fontSize: '12px',
                      cursor: 'auto',
                    }}
                  >
                    {t('Group Tour')}
                  </span>
                </div>

                <div className="p-3" style={{ background: '#f6f6f6' }}>
                  <p> {shorten(tour.overview, 75) + ' ...'} </p>
                </div>

                <Row className="align-items-center">
                  <Col className="ml-2" xs={3}>
                    <p className="text-left mb-0">{t('From')}</p>

                    <p
                      className="text-left seorgesb mb-0"
                      style={{
                        fontSize: '25px',
                        color: '#44444',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ${tour.price}
                    </p>
                  </Col>
                  <Col xs={1}>
                    <div
                      className="mt-3"
                      style={{
                        height: '40px',
                        borderLeft: '1px solid #d6d6d6',
                      }}
                    ></div>
                  </Col>

                  <Col xs={3}>
                    <p className="text-center seorgesb mb-0" style={{ fontSize: '25px' }}>
                      {tour.duration}
                    </p>
                    <p className="text-center pt-1 mb-0">{t('Days')}</p>
                  </Col>

                  <Col xs={1}>
                    <div
                      className="mt-3"
                      style={{
                        height: '40px',
                        borderLeft: '1px solid #d6d6d6',
                      }}
                    ></div>
                  </Col>

                  <Col xs={3} className="mx-0">
                    {' '}
                    <p
                      className="seorge text-left px-0 mb-0"
                      style={{
                        color: '#525252',
                        fontSize: '14px',
                      }}
                    >
                      {tour.service_level}
                    </p>
                  </Col>
                </Row>

                <div style={{ background: '#f6f6f6' }}>
                  <Row>
                    <Col>
                      <span
                        style={
                          isCompareSelected[index]
                            ? {
                                cursor: 'pointer',
                                color: '#521231',
                                fontWeight: 'bold',
                              }
                            : {
                                cursor: 'pointer',
                                color: 'black',
                              }
                        }
                        onClick={() => handleAddToCompare(tour.id, index)}
                      >
                        {' '}
                        <MdCompareArrows className="our_color pl-2 mt-2" /> <span>{t('Compare')}</span>
                      </span>
                    </Col>
                    <Col>
                      <Link className="mt-2" href={`/tours/${tour.id}`}>
                        <Button
                          className="btn btn-sm mr-2 mt-1 font-weight-bold pull-right"
                          style={{
                            background: '#009FFF',
                            border: '1px solid #009FFF',
                            color: 'white',
                            fontSize: '13px',
                          }}
                        >
                          {t('Check details')}
                        </Button>
                      </Link>
                    </Col>
                  </Row>
                </div>
              </Card> */}
            </Col>
          ))}

          {allFilterableTours.custom_tours?.map((tour, index) => (
            <Col className="mb-3" xs={12} md={6} lg={4} key={tour.id}>
              <TourCard
                id={tour.id}
                image={`https://api.iranjourney.com/${map_view ? tour.map_thumbnail : tour.banner_thumbnail}`}
                title={tour.title}
                description={tour.overview}
                duration={tour.duration}
                price={tour.price}
                extraCation={
                  <span
                    className="tw-flex tw-items-center"
                    style={
                      isCompareSelected[index]
                        ? {
                            cursor: 'pointer',
                            color: '#521231',
                            fontWeight: 'bold',
                          }
                        : {
                            cursor: 'pointer',
                            color: 'black',
                          }
                    }
                    onClick={() => handleAddToCompare(tour.id, index)}
                  >
                    {' '}
                    <MdCompareArrows size={22} className="our_color pl-2 me-2" /> <span>{t('Compare')}</span>
                  </span>
                }
                shouldImageBePriority={index === 0 ? true : false}
              />

              {/* <Card
                className="text-left  mt-3 "
                style={
                  !isTabletOrMobile
                    ? {
                        display: 'inherit',

                        minHeight: '434px',
                        height: 'auto',
                        border: 'none',
                        boxShadow: '1px 2px #e3e1e1',
                      }
                    : {
                        display: 'inherit',
                        width: 'auto',
                        height: 'auto',
                        border: 'none',
                        boxShadow: '1px 2px #e3e1e1',
                      }
                }
              >
                <p
                  style={{
                    fontSize: '18px',
                    fontWeight: '550',
                  }}
                  className="montserrat mb-0 p-2"
                >
                  {' '}
                  {tour.title}
                </p>

                <div
                  style={
                    map_view
                      ? {
                          backgroundImage: `url(https://api.iranjourney.com/${
                            map_view ? tour.map_thumbnail : tour.banner_thumbnail
                          })`,
                          backgroundSize: 'cover',
                          minHeight: '254px',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                        }
                      : {
                          backgroundImage: `url(https://api.iranjourney.com/${
                            map_view ? tour.map_thumbnail : tour.banner_thumbnail
                          })`,
                          backgroundSize: 'cover',
                          minHeight: '172px',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                        }
                  }
                ></div>

                <div className="p-3" style={{ background: '#f6f6f6' }}>
                  <p>{tour.overview.length > 5 ? tour.overview.substring(0, 75) + ' ...' : tour.overview} </p>
                </div>

                <Row className="align-items-center">
                  <Col className="ml-2" xs={3}>
                    <p className="text-left mb-0">{t('From')}</p>

                    <p
                      className="text-left seorgesb mb-0"
                      style={{
                        fontSize: '25px',
                        color: '#44444',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ${tour.price}
                    </p>
                  </Col>
                  <Col xs={1}>
                    <div
                      className="mt-3"
                      style={{
                        height: '40px',
                        borderLeft: '1px solid #d6d6d6',
                      }}
                    ></div>
                  </Col>

                  <Col xs={3}>
                    <p className="text-center seorgesb mb-0" style={{ fontSize: '25px' }}>
                      {tour.duration}
                    </p>
                    <p className="text-center pt-1 mb-0">{t('Days')}</p>
                  </Col>

                  <Col xs={1}>
                    <div
                      className="mt-3"
                      style={{
                        height: '40px',
                        borderLeft: '1px solid #d6d6d6',
                      }}
                    ></div>
                  </Col>

                  <Col xs={3} className="mx-0">
                    {' '}
                    <p
                      className="seorge text-left px-0 mb-0"
                      style={{
                        color: '#525252',
                        fontSize: '14px',
                      }}
                    >
                      {tour.service_level}
                    </p>
                  </Col>
                </Row>

                <div style={{ background: '#f6f6f6' }}>
                  <Row>
                    <Col>
                      <span
                        style={
                          isCompareSelected[index]
                            ? {
                                cursor: 'pointer',
                                color: '#521231',
                                fontWeight: 'bold',
                              }
                            : {
                                cursor: 'pointer',
                                color: 'black',
                              }
                        }
                        onClick={() => handleAddToCompare(tour.id, index)}
                      >
                        {' '}
                        <MdCompareArrows size={30} className="our_color pl-2 mt-2" /> <span>{t('Compare')}</span>
                      </span>
                    </Col>
                    <Col>
                      <Link className="mt-2" href={`/tours/${tour.id}`}>
                        <Button
                          className="btn btn-sm mr-2 mt-1 font-weight-bold pull-right"
                          style={{
                            background: '#009FFF',
                            border: '1px solid #009FFF',
                            color: 'white',
                            fontSize: '13px',
                          }}
                        >
                          {t('Check details')}
                        </Button>
                      </Link>
                    </Col>
                  </Row>
                </div>
              </Card> */}
            </Col>
          ))}
        </Fragment>
      </Row>
    );

  return (
    <div className="tours-page">
      <Container fluid className="tw-pt-16">
        <Row className="mt-2 position-relative">
          <Col
            style={!isMobile ? { borderRight: '1px solid #e3e3e3' } : { border: 'none' }}
            className="px-5 d-md-none"
            lg={3}
          >
            <button
              className="btn-block"
              onClick={() => setShowAdvancedSearch(!show_advanced_search)}
              style={{
                color: 'white',
                background: '#5e0832',
                borderColor: '#5e0832',
                fontSize: '17px',
                fontWeight: 'bold',
                padding: '7px',
                borderRadius: '4px',
              }}
            >
              {!show_advanced_search ? 'Show Advanced Search' : 'Hide Advanced Search'}{' '}
              <i className="fa fa-filter" aria-hidden="true" />
            </button>
          </Col>
          {(show_advanced_search || !isMobile) && (
            <Col
              style={
                !isMobile
                  ? {
                      borderRight: '1px solid #e3e3e3',
                    }
                  : { border: 'none' }
              }
              className="position-relative"
              lg={3}
            >
              <b>Filter by: </b>
              <Form.Group className="my-3">
                <Form.Label htmlFor="languageChooser" className="mb-1 seorgesb">
                  {t('Language ')}
                </Form.Label>
                <Form.Control
                  id="languageChooser"
                  onChange={(e) => handleUpdateFilters(e.target.value, 'language')}
                  as="select"
                >
                  <option value="">{t('All languages')}</option>

                  {dropDownLanguages.isSuccess &&
                    dropDownLanguages.data?.language?.map((lng) => (
                      <option
                        key={lng.id}
                        value={lng.language__code}
                        defaultValue={
                          lng.language__code === filterQueries.language ? filterQueries.language : undefined
                        }
                      >
                        {lng.language__native_title}
                      </option>
                    ))}
                </Form.Control>{' '}
                <hr />
                <div>
                  <p className="mb-1 seorgesb"> {t('Duration ')} </p>
                  <div className="px-4 mb-5">
                    <Range
                      marks={{
                        1: `1 day`,
                        30: `30 day`,
                      }}
                      min={1}
                      max={30}
                      value={filterQueries.duration}
                      tipFormatter={(value) => `${value} day`}
                      // tipProps={{
                      //   placement: 'bottom',
                      //   visible: true,
                      // }}
                      onChange={(val1) => handleUpdateFilters(val1, 'duration')}
                      trackStyle={[{ backgroundColor: '#731a45' }]}
                      handleStyle={[{ backgroundColor: '#731a45' }]}
                      ariaLabelGroupForHandles={['min duration', 'max duration']}
                    />
                  </div>
                </div>
                <div className="pb-4">
                  <p className="seorgesb"> {t('Budget ')}</p>
                  <div className="px-4">
                    <Range
                      marks={{
                        50: `$50`,
                        10000: `$10000`,
                      }}
                      min={50}
                      max={10000}
                      value={filterQueries.budget}
                      tipFormatter={(value) => `$ ${value}`}
                      // tipProps={{
                      //   placement: 'bottom',
                      //   visible: true,
                      // }}
                      onChange={(val1) => handleUpdateFilters(val1, 'budget')}
                      trackStyle={[{ backgroundColor: '#731a45' }]}
                      handleStyle={[{ backgroundColor: '#731a45' }]}
                      ariaLabelGroupForHandles={['min budget', 'max budget']}
                    />
                  </div>
                </div>
                <hr />
                <div>
                  <p className="mb-2 seorgesb">{t('Travel Styles ')}</p>
                  {checkboxes.map((item) => (
                    <label className="d-block mb-1" key={item.value}>
                      <Checkbox
                        name={item.label}
                        checked={checkedItems.includes(item.value)}
                        onChange={() => handleChange(item.value)}
                      />
                      <span className="ml-1"> {item.label}</span>
                    </label>
                  ))}
                </div>
              </Form.Group>
            </Col>
          )}
          <Col md={9}>
            <Container className="text-center pt-2 position-relative">
              <SearchTourButtons setMapView={setMapView} map_view={map_view} handleResetFilters={handleResetFilters} />

              <hr />

              {content}

              {compare_tours.length > 0 && (
                <div
                  className="position-fixed"
                  style={{
                    bottom: '2%',
                    width: isMobile ? '80%' : '40%',
                    left: 0,
                    right: 0,
                    margin: 'auto',
                  }}
                >
                  <div
                    className="title d-flex align-items-center p-2"
                    style={{
                      cursor: 'pointer',
                      gap: 20,
                      backgroundColor: '#521332',
                      color: 'white',
                    }}
                    onClick={() => setOpen(!open)}
                  >
                    <div className="icon">{open ? <BiChevronUp size={25} /> : <BiChevronDown size={25} />}</div>
                    <div className="count-selected">
                      <b>Trip comparison ({compare_tours.length})</b>
                    </div>
                  </div>
                  <Collapse in={open}>
                    <div className="selected-tours-for-compare bg-white">
                      <div className="p-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <h5>Compare up to 5 tours</h5>
                          <Button variant="light" onClick={handleResetCompare}>
                            Reset
                          </Button>
                        </div>
                        <ul className="list-unstyled px-0 py-2 m-0">
                          {compare_tours.length > 0 ? (
                            compare_tours.map((tour) => (
                              <li className="d-flex align-items-center justify-content-between" key={tour.id}>
                                <b>
                                  {allFilterableTours.custom_tours.find((item) => item.id === tour)?.title ||
                                    allFilterableTours.group_tours.find((item) => item.id === tour)?.title}
                                </b>
                                <Button
                                  variant=""
                                  className="text-black-50"
                                  onClick={() => handleRemoveFromCompare(tour)}
                                >
                                  <BiTrash size={25} />
                                </Button>
                              </li>
                            ))
                          ) : (
                            <p>No tour selected.</p>
                          )}
                        </ul>
                      </div>
                      <div
                        className="title text-center p-2"
                        style={{
                          cursor: 'pointer',
                          gap: 20,
                          backgroundColor: '#521332',
                          color: 'white',
                        }}
                      >
                        <Link
                          className="text-white d-block"
                          href={`/${lang}/tours/compare?items=${Array.from(new Set(compare_tours)).toString()}`}
                        >
                          Compare
                        </Link>
                      </div>
                    </div>
                  </Collapse>
                </div>
              )}
            </Container>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Client;
